import { defineStore } from 'pinia';
import {
    AssignedTask,
    Class,
    Group,
    GroupUpdate,
    Message,
    MessageCreate,
    Notice,
    ParsedUser,
    PupilOverview,
    PupilTask,
    School,
    ShareableTeacher,
    TaskAssignment,
    TaskAssignmentResponse,
    TaskStats,
    Teacher,
    TeacherWithSharing,
} from '@ilteducation/giglets-types';

export const useTeacherStore = defineStore('teacher', () => {
    const teacher = ref<Teacher>();
    const school = ref<School>();
    const messages = ref<Message[]>([]);
    const pupilTasks = ref<PupilTask[]>([]);
    const assignedTasks = ref<AssignedTask[]>([]);
    const pupils = ref<PupilOverview[]>([]);
    const classes = ref<Class[]>([]);
    const groups = ref<Group[]>([]);
    const taskStats = ref<TaskStats>();
    const featuredBooks = ref<number[]>([]);
    const notices = ref<Notice[]>([]);
    const sharedMarking = ref<TeacherWithSharing>();
    const shareableTeachers = ref<ShareableTeacher[]>([]);

    const isLoaded = ref<Record<string, boolean>>({});
    const isLoading = ref<Record<string, boolean>>({});

    const state = {
        teacher,
        school,
        messages,
        pupilTasks,
        assignedTasks,
        pupils,
        classes,
        groups,
        taskStats,
        featuredBooks,
        notices,
        sharedMarking,
        shareableTeachers,
    };

    function fetchTeacher(): Promise<Ref<Teacher>> {
        return load('/api/teacher/current', 'teacher', teacher);
    }
    function fetchSchool(): Promise<Ref<School>> {
        return load('/api/teacher/current/school', 'school', school);
    }
    function fetchMessages(): Promise<Ref<Message[]>> {
        return load('/api/teacher/current/messages', 'messages', messages);
    }
    function fetchPupilTasks(): Promise<Ref<PupilTask[]>> {
        return load('/api/teacher/current/pupil_tasks', 'pupilTasks', pupilTasks);
    }
    function fetchAssignedTasks(): Promise<Ref<AssignedTask[]>> {
        return load('/api/teacher/current/tasks/assigned', 'assignedTasks', assignedTasks);
    }
    function fetchPupils(): Promise<Ref<PupilOverview[]>> {
        return load('/api/teacher/current/pupils', 'pupils', pupils);
    }
    function fetchClasses(): Promise<Ref<Class[]>> {
        return load('/api/teacher/current/classes', 'classes', classes);
    }
    function fetchGroups(): Promise<Ref<Group[]>> {
        return load('/api/teacher/current/groups', 'groups', groups);
    }
    function fetchTaskStats(): Promise<Ref<TaskStats>> {
        return load('/api/teacher/current/tasks/task_stats', 'taskStats', taskStats);
    }
    function fetchFeaturedBooks(): Promise<Ref<number[]>> {
        return load('/api/teacher/featured_books', 'featuredBooks', featuredBooks);
    }
    function fetchNotices(): Promise<Ref<Notice[]>> {
        return load('/api/teacher/notices', 'notices', notices);
    }
    function fetchSharedMarking(): Promise<Ref<TeacherWithSharing>> {
        return load('/api/teacher/current/shared_marking', 'sharedMarking', sharedMarking);
    }
    function fetchShareableTeachers(): Promise<Ref<ShareableTeacher[]>> {
        return load('/api/teacher/current/shared_marking/shareable_teachers', 'shareableTeachers', shareableTeachers);
    }

    async function updateSharedMarking(teacherIds: number[]) {
        await $fetch('/api/teacher/current/shared_marking', {
            method: 'PUT',
            body: { teacherIds },
        });
        isLoaded.value.shareableTeachers = false;
        isLoaded.value.sharedMarking = false;
    }

    async function updateTeacher(newData: Partial<Teacher>): Promise<Ref<Teacher | undefined>> {
        teacher.value = { ...teacher.value, ...newData } as Teacher;
        teacher.value = await $fetch<Teacher>('/api/teacher/current', {
            method: 'PUT',
            body: newData,
        });
        return teacher;
    }

    async function updatePassword(oldPassword: string, newPassword: string) {
        await $fetch<Teacher>('/api/teacher/current/password', {
            method: 'PUT',
            body: { newPassword, oldPassword },
        });
    }

    async function updateTaskAssignments(assignments: TaskAssignment[]): Promise<TaskAssignmentResponse> {
        const res = await $fetch('/api/teacher/current/tasks/assigned', {
            method: 'PUT',
            body: assignments,
        });
        assignedTasks.value = [];
        pupilTasks.value = [];
        isLoaded.value.assignedTasks = false;
        isLoaded.value.pupilTasks = false;
        return res;
    }

    async function createGroup(groupName: string) {
        isLoaded.value.groups = false;
        const newGroup = await $fetch<Group>('/api/teacher/current/groups/', {
            method: 'POST',
            body: { name: groupName },
        });
        groups.value = [...groups.value, newGroup];
        isLoaded.value.groups = true;
        return newGroup;
    }

    async function updateGroup(groupUid: string, group: GroupUpdate) {
        isLoaded.value.groups = false;
        const updatedGroup = await $fetch<Group>(`/api/teacher/current/groups/${groupUid}`, {
            method: 'PUT',
            body: group,
        });
        pupils.value.forEach((p) => {
            if (group.addPupils?.includes(p.uid)) {
                p.groups.push({ uid: updatedGroup.uid, name: updatedGroup.name });
            }
            if (group.removePupils?.includes(p.uid)) {
                p.groups = p.groups.filter((g) => g.uid !== updatedGroup.uid);
            }
        });
        groups.value = groups.value.map((g) => (g.uid === groupUid ? updatedGroup : g));
        isLoaded.value.groups = true;
        return updatedGroup;
    }

    async function deleteGroup(groupUid: string) {
        isLoaded.value.groups = false;
        await $fetch(`/api/teacher/current/groups/${groupUid}`, {
            method: 'DELETE',
        });
        pupils.value.forEach((p) => {
            p.groups = p.groups.filter((g) => g.uid !== groupUid);
        });
        groups.value = groups.value.filter((g) => g.uid !== groupUid);
        isLoaded.value.groups = true;
    }

    async function updatePupils(pupilsBatch: Record<string, Partial<{ show_audio: boolean; password: string }>>) {
        await $fetch<PupilOverview[]>(`/api/teacher/current/pupils`, {
            method: 'POST',
            body: pupilsBatch,
        });
        pupils.value = pupils.value.map((pupil) => ({ ...pupil, ...(pupilsBatch[pupil.id] ?? {}) }));
    }

    async function updatePupilTask(pupilTaskId: number, newData: Partial<PupilTask>) {
        isLoaded.value.pupilTasks = false;
        isLoaded.value.assignedTasks = false;
        const updated = await $fetch<PupilTask>(`/api/teacher/current/pupil_tasks/${pupilTaskId}`, {
            method: 'PUT',
            body: newData,
        });
        pupilTasks.value = pupilTasks.value.map((p) => (p.id === pupilTaskId ? updated : p));
        isLoaded.value.pupilTasks = true;
        return updated;
    }

    async function reassignTask(pupilTaskId: number, reassignComment?: string) {
        isLoaded.value.pupilTasks = false;
        isLoaded.value.assignedTasks = false;

        const newPupilTask = await $fetch<PupilTask>(`/api/teacher/current/tasks/reassign`, {
            method: 'POST',
            body: { pupilTaskId, reassignComment },
        });
        await fetchPupilTasks();
        isLoaded.value.pupilTasks = true;
        return newPupilTask;
    }

    async function addPupils(users: ParsedUser[]) {
        isLoaded.value.pupils = false;
        const newUsers = await $fetch<PupilOverview[]>(`/api/pupils`, {
            method: 'POST',
            body: users,
        });
        pupils.value.push(...newUsers);
        isLoaded.value.pupils = true;
    }

    async function createMessage(msg: MessageCreate) {
        const newMessage: Message = await $fetch('/api/teacher/current/messages', {
            method: 'POST',
            body: msg,
        });
        if (messages.value) {
            messages.value.push(newMessage);
        } else {
            messages.value = [newMessage];
        }
    }

    async function markMessageRead(messageId: number) {
        await $fetch('/api/teacher/current/messages/' + messageId + '/read', {
            method: 'PUT',
        });
        const msg = messages.value?.find((m) => m.id === messageId);
        if (msg) {
            msg.msg_read = true;
        }
    }

    async function deleteMessage(messageId: number) {
        await $fetch('/api/teacher/current/messages/' + messageId, {
            method: 'DELETE',
        });
        const msgIdx = messages.value?.findIndex((m) => m.id === messageId);
        messages.value.splice(msgIdx, 1);
    }

    async function load<T>(url: string, name: keyof typeof state, ref: Ref<T | undefined>): Promise<Ref<T>> {
        if (isLoaded.value[name]) {
            return Promise.resolve(ref as Ref<T>);
        }
        if (isLoading.value[name]) {
            return new Promise<Ref<T>>((resolve) => {
                const unwatch = watch(isLoaded.value, () => {
                    if (isLoaded.value[name]) {
                        unwatch();
                        resolve(ref as Ref<T>);
                    }
                });
            });
        }
        try {
            isLoading.value[name] = true;
            ref.value = await $fetch(url);
            isLoaded.value[name] = true;
            return Promise.resolve(ref as Ref<T>);
        } finally {
            isLoading.value[name] = false;
        }
    }

    function pupilByUid(pupilUid?: string): PupilOverview | undefined {
        if (!pupilUid) {
            return undefined;
        }
        return pupils.value.find((p) => p.uid === pupilUid);
    }

    function pupilName(uid: string | { uid: string } | undefined): string {
        if (!uid) {
            return '';
        }
        const pupilUid = typeof uid === 'string' ? uid : uid.uid;
        const pupil = pupils.value.find((p) => p.uid === pupilUid);
        if (state.teacher.value?.virtual_pupil.id === pupil?.id) {
            return 'My virtual pupil';
        }
        return pupil?.name || '';
    }

    return {
        ...state,
        isLoaded,
        isLoading,
        fetchTeacher,
        fetchSchool,
        fetchMessages,
        fetchPupilTasks,
        fetchAssignedTasks,
        fetchPupils,
        fetchClasses,
        fetchGroups,
        fetchTaskStats,
        fetchFeaturedBooks,
        fetchNotices,
        fetchSharedMarking,
        fetchShareableTeachers,
        updateSharedMarking,
        updateTeacher,
        updatePassword,
        updateTaskAssignments,
        updatePupils,
        updatePupilTask,
        createGroup,
        updateGroup,
        deleteGroup,
        reassignTask,
        addPupils,
        createMessage,
        markMessageRead,
        deleteMessage,
        pupilByUid,
        pupilName,
    };
});
